import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { 
  signInWithPopup, 
  signOut as firebaseSignOut,
  onAuthStateChanged,
  signInWithCredential,
  GoogleAuthProvider
} from 'firebase/auth';
import { auth, googleProvider } from '../config/firebase';
import { Capacitor } from '@capacitor/core';
import { GoogleAuth } from '@southdevs/capacitor-google-auth';
import { userAPI } from '../config/userApi';
import { registerDevice } from '../utils/deviceUtils';
import { API_BASE_URL } from '../config.js';
import { clearUserDataCaches } from '../utils/dataCache';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [userPermissions, setUserPermissions] = useState(null); // V46: permissions from /api/user/me
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    console.log('🔄 AuthContext mounting...');
    console.log('📡 About to call onAuthStateChanged...');
    console.log('🔥 Auth object:', auth);
    console.log('🔥 onAuthStateChanged function:', typeof onAuthStateChanged);
    
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      console.log('⚡⚡⚡ CALLBACK FIRED! User:', user);  // ← THÊM DÒNG NÀY
      try {
        console.log('🔔 Auth state changed:', user ? user.email : 'No user');
        
        setCurrentUser(user);
        
        if (user) {
          console.log('👤 User detected, fetching profile...');
          
          // Fetch profile directly here - no separate function
          try {
            console.log('🔍 Fetching user profile for:', user.email);
            // Backend trả hồ sơ và TỰ TẠO (gói FREE dùng thử) nếu là lần đăng nhập đầu
            const profile = await userAPI.getUserProfile();
            if (!profile) {
              console.warn('⚠️ Không lấy được hồ sơ người dùng từ máy chủ');
            }

            setUserProfile(profile);
            
            // V46: Fetch user permissions from backend API
            try {
              const token = await user.getIdToken();
              const baseUrl = API_BASE_URL;
              const response = await fetch(`${baseUrl}/api/user/me`, {
                headers: {
                  'Authorization': `Bearer ${token}`,
                  'Content-Type': 'application/json'
                }
              });
              
              if (response.ok) {
                const meData = await response.json();
                console.log('✅ Fetched user permissions:', meData.data?.permissions);
                setUserPermissions(meData.data || null);
              } else {
                console.warn('⚠️ Failed to fetch user permissions:', response.status);
                setUserPermissions(null);
              }
            } catch (permError) {
              console.warn('⚠️ Error fetching user permissions:', permError.message);
              setUserPermissions(null);
            }
            
            // Register device for tracking (v2.9.0)
            try {
              const token = await user.getIdToken();
              await registerDevice(API_BASE_URL, token);
            } catch (deviceError) {
              console.warn('Device registration failed:', deviceError.message);
            }
          } catch (profileError) {
            console.error('❌ Error fetching user profile:', profileError);
            console.error('Error details:', profileError.message);
          }
        } else {
          console.log('❌ No user, clearing profile...');
          setUserProfile(null);
          setUserPermissions(null); // V46: clear permissions too
        }
        
        console.log('✅ Auth state processing complete');
        setLoading(false);
      } catch (error) {
        console.error('❌ CRITICAL ERROR in auth listener:', error);
        console.error('Error message:', error.message);
        console.error('Error stack:', error.stack);
        setLoading(false);
      }
    }, (error) => {
      console.error('❌ onAuthStateChanged initialization error:', error);
      setLoading(false);
    });

    return () => {
      console.log('🧹 Cleaning up auth listener');
      unsubscribe();
    };
  }, []); // EMPTY - no dependencies!

  const signInWithGoogle = async () => {
    try {
      const isNative = Capacitor.isNativePlatform();
      const platform = Capacitor.getPlatform();
      
      console.log('🔍 Platform Detection Results:');
      console.log('  - isNativePlatform():', isNative);
      console.log('  - getPlatform():', platform);
      
      if (isNative) {
        console.log('🚀 Using Native Google Sign-In');
        
        await GoogleAuth.initialize({
          clientId: '564302221086-c8khniq88al7pba9987e2h8af357rdro.apps.googleusercontent.com',
          scopes: ['profile', 'email'],
          grantOfflineAccess: true,
        });
        
        const googleUser = await GoogleAuth.signIn();
        
        if (!googleUser?.authentication?.idToken) {
          throw new Error('Invalid Google Auth response - missing idToken');
        }
        
        const credential = GoogleAuthProvider.credential(
          googleUser.authentication.idToken,
          googleUser.authentication.accessToken
        );
        
        const result = await signInWithCredential(auth, credential);
        console.log('✅ Firebase sign-in successful:', result.user.email);
        
        return result.user;
      } else {
        console.log('🌐 Using web Google Sign-In with account selection');
        
        // Force Google to show account picker
        googleProvider.setCustomParameters({
          prompt: 'select_account'  // ← KEY: Force chọn tài khoản
        });
        
        const result = await signInWithPopup(auth, googleProvider);
        console.log('✅ Web sign-in successful:', result.user.email);
        return result.user;
      }
    } catch (error) {
      console.error('💥 Authentication error:', error);
      throw error;
    }
  };

  const signOut = async () => {
    try {
      console.log('🚪 Starting sign out process...');
      clearUserDataCaches(currentUser?.email); // không để dữ liệu của tài khoản này lại trên máy dùng chung
      
      // 1. Sign out from Google FIRST (to clear Google session)
      if (Capacitor.isNativePlatform()) {
        try {
          console.log('📱 Signing out from Google on Android...');
          await GoogleAuth.signOut();
          console.log('✅ Google sign out successful');
        } catch (googleSignOutError) {
          console.error('❌ Google Auth sign out failed:', googleSignOutError);
          // Continue anyway
        }
      } else {
        // On web - revoke Google access token
        try {
          console.log('🌐 Revoking Google access on web...');
          const googleSignOut = window.gapi?.auth2?.getAuthInstance()?.signOut();
          if (googleSignOut) {
            await googleSignOut;
            console.log('✅ Google web sign out successful');
          }
        } catch (webGoogleError) {
          console.error('❌ Web Google sign out failed:', webGoogleError);
          // Continue anyway
        }
      }
      
      // 2. Sign out from Firebase
      console.log('🔥 Signing out from Firebase...');
      await firebaseSignOut(auth);
      console.log('✅ Firebase sign out successful');
      
      // 3. Clear user state
      setCurrentUser(null);
      setUserProfile(null);
      
      console.log('✅ Sign out complete!');
    } catch (error) {
      console.error('💥 Error during sign out:', error);
      throw error;
    }
  };

  const refreshUserProfile = async () => {
    if (currentUser) {
      // Inline profile refresh
      try {
        const profile = await userAPI.getUserProfile();
        if (profile) setUserProfile(profile); // lỗi mạng: giữ hồ sơ cũ, không xoá
      } catch (error) {
        console.error('Error refreshing profile:', error);
      }
    }
  };

  const updateUserSettings = async (settings) => {
    if (!currentUser?.email) return null;
    
    try {
      const updatedProfile = await userAPI.updateUserSettings(currentUser.email, settings);
      setUserProfile(updatedProfile);
      return updatedProfile;
    } catch (error) {
      console.error('❌ Error updating user settings:', error);
      throw error;
    }
  };

  // V46 FIX 2: Refresh permissions từ /api/user/me
  const refreshPermissions = async () => {
    if (!currentUser) return;
    
    try {
      const token = await currentUser.getIdToken();
      const baseUrl = API_BASE_URL;
      const response = await fetch(`${baseUrl}/api/user/me`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const meData = await response.json();
        console.log('✅ Refreshed permissions:', meData.data?.permissions);
        setUserPermissions(meData.data || null);
        return meData.data || null;
      }
      console.warn('⚠️ Failed to refresh permissions:', response.status);
    } catch (error) {
      console.warn('⚠️ refreshPermissions error:', error.message);
    }
    return null;
  };

  // Làm mới gói + quyền từ server (khi admin vừa đổi gói / gia hạn): không cần đăng xuất.
  // Gọi khi: quay lại tab, mỗi 5 phút, mở trang Đăng ký, hoặc vừa bị chặn bởi giới hạn gói.
  const refreshAccountState = async () => {
    if (!currentUser) return null;
    const [, permissions] = await Promise.all([refreshUserProfile(), refreshPermissions()]);
    return permissions;
  };

  // Bản mới nhất của hàm (effect bên dưới chỉ đăng ký 1 lần theo currentUser)
  const refreshAccountStateRef = useRef(refreshAccountState);
  refreshAccountStateRef.current = refreshAccountState;

  useEffect(() => {
    if (!currentUser) return undefined;
    let lastRun = Date.now();
    const run = (minGapMs) => {
      if (Date.now() - lastRun < minGapMs) return;
      lastRun = Date.now();
      refreshAccountStateRef.current().catch(() => {});
    };
    const onVisible = () => { if (document.visibilityState === 'visible') run(60 * 1000); };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onVisible);
    const timer = setInterval(() => { if (document.visibilityState === 'visible') run(4 * 60 * 1000); }, 5 * 60 * 1000);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onVisible);
      clearInterval(timer);
    };
  }, [currentUser]);

  const value = {
    currentUser,
    userProfile,
    userPermissions, // V46: add permissions to context
    signInWithGoogle,
    signOut,
    loading,
    refreshUserProfile,
    refreshPermissions, // V46 FIX 2: export refresh permissions
    refreshAccountState,
    updateUserSettings,
    isAdmin: userProfile?.role === 'ADMIN',
    isActive: userProfile?.status === 'ACTIVE',
    isPending: userProfile?.status === 'PENDING',
    isBanned: userProfile?.status === 'BANNED'
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};

export default AuthContext;